<template>
  <video-player
    ref="cutPlayer"
    class="edit-form-player"
    :options="playerOptions"
    :thumbnail="thumbnailLink"
    :src="src"
    :muted.sync="muted"
    :paused="paused"
    :height="height"
    :width="width"
    :original-size="originalSize"
    @durationchange="duration = $event"
    @timeupdate="time = $event"
    @paused="$emit('updatePausedTrue', true)"
    @played="$emit('updatePausedFalse', false)"
    @loading="isLoading()"
    @loaded="isLoaded()"
  />
</template>

<script>
import { mapState } from "vuex";
import VideoPlayer from "@/components/media/video/VideoPlayer.vue";
import { MODES_DRAG, MODES_PREVIEW } from "./cut_modes";

// @group Editing
// Reactive player for edit panel
export default {
  name: "EditPlayer",
  components: {
    VideoPlayer
  },
  model: {
    prop: "playerData",
    event: "playerDataChanged"
  },
  props: {
    originalSize: {
      type: Boolean,
      default: false
    },
    cropX: {
      type: Number,
      default: 0
    },
    cropY: {
      type: Number,
      default: 0
    },
    cropWidth: {
      type: Number,
      default: 0
    },
    cropHeight: {
      type: Number,
      default: 0
    },
    mode: {
      type: String,
      default: "drag"
    },
    startPreviewEmitValue: {
      type: Number,
      default: 0
    },
    playerTime: {
      type: Number,
      default: 0
    },
    playerStartTime: {
      type: Number,
      default: 0
    },
    // Player data object
    playerData: {
      type: Object,
      required: true
    },
    paused: {
      type: Boolean,
      required: true
    },
    // Thumbnail link
    thumbnail: {
      type: String,
      default: null
    },
    // Source video link
    src: {
      type: [Object, String],
      required: true
    },
    // Synchronized muted flag
    isMuted: {
      type: Boolean,
      default: false
    },
    cropVideoResize: {
      type: Boolean,
      default: false
    },
    stopPreview: {
      type: Boolean,
      default: false
    },
    // Video width and heigth in pixels
    videoSizes: {
      type: Object,
      required: true,
      vaidator(val) {
        const { width, height } = val;
        return Number.isInteger(width) && Number.isInteger(height);
      }
    },
    hideControls: {
      type: Boolean,
      default: false
    },
  },
  /** VideoJS player options */
  playerOptions: {
    /** Autoplay need to initialize player area at start,
     * otherwise crop/text/image areas will can go beyound
     * player boundary */
    autoplay: true,
    controls: true,
    fill: true,
    preload: "metadata",
    cors: true,
    children: {
      controlBar: {
        captionsButton: false,
        chaptersButton: false,
        fullscreenToggle: false,
        subtitlesButton: false,
        playbackRateMenuButton: false,
        pictureInPictureToggle: false
      }
    }
  },
  data() {
    return {
      playerOptions: {
        /** Autoplay need to initialize player area at start,
         * otherwise crop/text/image areas will can go beyound
         * player boundary */
        autoplay: true,
        controls: !this.hideControls,
        fill: true,
        preload: "metadata",
        cors: true,
        children: {
          controlBar: {
            captionsButton: false,
            chaptersButton: false,
            fullscreenToggle: false,
            subtitlesButton: false,
            playbackRateMenuButton: false,
            pictureInPictureToggle: false
          }
        }
      },
      stopPreviewWatch: null,
      cancelStartJumpWatcher: null,
      time: 0,
      // paused: this.playerData.paused,
      loading: true,
      duration: 10,
      // Player size settings
      relativeMaxWidth: 0.8,
      relativeMaxHeight: 0.5,
      width: 800,
      height: 640
    };
  },
  mounted() {
    this.cancelStartJumpWatcher = this.$watch('loading', (isLoading) => {
      if(isLoading === false) {
        this.$refs.cutPlayer.setTime(this.playerStartTime);
        this.cancelStartJump();
      }
    });
  },
  computed: {
    ...mapState({
      thumbs: state => state.services.thumbs,
      host: state => state.services.host,
      vod: state => state.services.vod
    }),
    // @vuese
    // Thumbnail URL
    thumbnailLink() {
      return `${this.thumbnail}`;
    },
    // @vuese
    // Muted synchronized property
    muted: {
      get() {
        return this.isMuted;
      },
      set(value) {
        this.$emit("update:isMuted", value);
      }
    },
    // @vuese
    // Is player paused or not
    playerPaused() {
      return this.playerData?.paused;
    },
    // @vuese
    // Duration that rounded down to make it integer
    flooredDuration() {
      return Math.floor(this.videoDuration * 1000) / 1000;
    },
    // @vuese
    // Return array of inverse ranges (not selected areas) to made inverse cut
    inverseRanges() {
      const inverse = [];

      let lastEnd = 0;
      for (const [start, end] of this.ranges) {
        if (lastEnd !== start) {
          inverse.push([lastEnd, start]);
        }

        lastEnd = end;
      }

      if (lastEnd < this.flooredDuration) {
        inverse.push([lastEnd, this.flooredDuration]);
      }

      return inverse;
    }
  },
  watch: {
    ranges(newRanges, oldRanges) {
      if (newRanges.length !== oldRanges.length) {
        /** it's only add/delete range, no need to sync player */
        return;
      } else {
        /** sync player with changed time */
        for (let i = 0; i < newRanges.length; i++) {
          for (let j = 0; j < 2; j++) {
            const oldPointer = oldRanges[i][j];
            const newPointer = newRanges[i][j];
            if (oldPointer !== newPointer) {
              this.playerTime = newPointer;
              this.playerPaused = true;
            }
          }
        }
      }
    },
    // @vuese
    // Update player time video segment
    playerTime() {
      this.cancelStartJump();
      this.$refs.cutPlayer.setTime(this.playerTime);
    },
    // @vuese
    // Update player time info
    time() {
      this.$emit("timeUpdate", this.time);
      this.updateData();
    },
    // @vuese
    // Update player paused info
    paused() {
      this.updateData();
    },
    // @vuese
    // Update player loading info
    loading() {
      this.updateData();
    },
    // @vuese
    // Update player duration info
    duration() {
      this.updateData();
    },
    // @vuese
    // Update stop/play player
    playerPaused(paused) {
      if (this.paused != paused) {
        this.paused = paused;
      }
    },
    stopPreview() {
      /*
      if(this.stopPreview == true){
        this.paused = true;
      }
      */
    }
  },
  methods: {
    // @vuese
    // Sync 'playerData' property value
    updateData() {
      // Updates data
      this.$emit("playerDataChanged", {
        time: this.time,
        paused: this.paused,
        loading: this.loading,
        duration: this.duration
      });
    },
    open() {
      this.renderPlayer = true;
    },
    handleOpened() {
      this.syncPlayerTime();
      this.unwatchPlayerTime = this.$watch("playerTime", (newTime, oldTime) => {
        if (Math.abs(newTime - oldTime) > 0.5) {
          this.syncPlayerTime();
        }
      });
    },
    // @vuese
    // Close modal
    close() {
      this.$emit("update:visible", false);
      // Modal closed by user
      this.$emit("closed");
      this.unwatchPlayerTime();
      if (this.mode === MODES_PREVIEW) {
        this.stopPreview();
      }
    },
    // @vuese
    // Start cutting proccess
    // @arg inverse - is crop inverse or normal
    startCut(inverse) {
      if (this.isLoading) return;
      this.isLoading = true;

      let ranges = null;
      let done = null;

      // if (!inverse) {
      ranges = this.ranges;
      done = this.finishCut;
      // } else {
      //   ranges = this.inverseRanges;
      done = () => {
        this.ranges = [[0, this.flooredDuration]];
        this.isLoading = false;
        this.close();
      };
      // }

      this.$emit("cut", {
        inverse,
        ranges,
        done,
        transitionType: this.transitionType,
        failed: () => {
          this.isLoading = false;
        }
      });
    },
    // @vuese
    // Handle cut process finishing
    // @arg successes - list of boolean success flags for every range
    finishCut(successes) {
      const restRanges = [];

      for (let i = 0; i < this.ranges.length; i++) {
        if (!successes[i]) {
          restRanges.push(this.ranges[i]);
        }
      }

      /** if all ranges cut successfully -> close modal & add default range */
      if (restRanges.length === 0) {
        this.close();
        this.ranges = [[0, this.flooredDuration]];
      } else {
        this.ranges = restRanges;
      }

      this.isLoading = false;
    },
    syncPlayerTime() {
      //this.$refs.cutPlayer.setTime(this.playerTime);
    },
    setTime(time) {
      this.time = time;
      // this.playerTime = time;
      this.$refs.cutPlayer.setTime(time);
      this.playerData.time = time;
    },
    startMode(mode) {
      this.mode = mode;
    },

    isLoading() {
      this.loading = true;
    },
    isLoaded() {
      this.loading = false;
    },
    cancelStartJump() {
      if(this.cancelStartJumpWatcher !== null) {
        this.cancelStartJumpWatcher();
        this.cancelStartJumpWatcher = null;
      }
    },
  }
};
</script>

<style scoped>
.edit-form-player {
  height: 100%;
  max-width: 100%;
}
</style>

<style>
.edit-form-player .vjs-tech {
  /* width: auto !important; */
  width: 100%;
  height: 100%;
}
</style>
